import React, { useState, useEffect } from 'react';
import { SocialPost, URLPreview, socialService } from '../../services/socialService';
import { parseContentWithMentions } from '../../utils/mentionUtils';
import './PostCard.css';

interface PostCardProps {
  post: SocialPost;
  currentUserId?: number;
  isEditing?: boolean;
  editContent?: string;
  onEditContentChange?: (content: string) => void;
  onStartEdit?: () => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
  onDelete?: () => void;
  onLikeToggle?: () => void;
  onCommentClick?: () => void;
  maxChars?: number;
  showBackButton?: boolean;
  onBackClick?: () => void;
  isWaitingForAI?: boolean;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  currentUserId,
  isEditing = false,
  editContent = '',
  onEditContentChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onLikeToggle,
  onCommentClick,
  maxChars = 500,
  showBackButton = false,
  onBackClick,
  isWaitingForAI = false,
}) => {
  const [urlPreviews, setUrlPreviews] = useState<URLPreview[]>([]);
  const [loadingPreviews, setLoadingPreviews] = useState(false);

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  const extractUrls = (text: string): string[] => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const matches = text.match(urlRegex);
    return matches || [];
  };

  useEffect(() => {
    const fetchPreviews = async () => {
      const urls = extractUrls(post.content);
      if (urls.length === 0) {
        setUrlPreviews([]);
        return;
      }

      setLoadingPreviews(true);
      const previews: URLPreview[] = [];

      for (const url of urls.slice(0, 3)) { // Limit to 3 previews max
        const preview = await socialService.fetchUrlPreview(url);
        if (preview) {
          previews.push(preview);
        }
      }

      setUrlPreviews(previews);
      setLoadingPreviews(false);
    };

    if (!isEditing) {
      fetchPreviews();
    }
  }, [post.content, isEditing]);

  const isOwnPost = currentUserId === post.user_id;

  return (
    <div className="postcard-container">
      {showBackButton && onBackClick && (
        <button onClick={onBackClick} className="postcard-back-link">
          ← Back to Social
        </button>
      )}
      
      <div className="postcard-card">
        <div className="postcard-header">
          <div className="postcard-author">
            {post.profile_picture_url ? (
              <img 
                src={post.profile_picture_url} 
                alt={post.display_name || post.username}
                className="postcard-avatar"
              />
            ) : (
              <div className="postcard-avatar-placeholder">
                {(post.display_name || post.username || 'U')[0].toUpperCase()}
              </div>
            )}
            <div className="postcard-author-info">
              <span className="postcard-author-name">
                {post.display_name || post.username || 'Anonymous'}
              </span>
              <span className="postcard-time">{formatTimeAgo(post.created_at)}</span>
            </div>
          </div>
          {isOwnPost && (
            <div className="postcard-menu">
              {isEditing ? (
                <>
                  <button 
                    className="postcard-menu-btn postcard-save-btn"
                    onClick={onSaveEdit}
                  >
                    Save
                  </button>
                  <button 
                    className="postcard-menu-btn postcard-cancel-btn"
                    onClick={onCancelEdit}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="postcard-menu-btn"
                    onClick={onStartEdit}
                  >
                    Edit
                  </button>
                  <button 
                    className="postcard-menu-btn postcard-delete-btn"
                    onClick={onDelete}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="postcard-content">
          {isEditing ? (
            <textarea
              value={editContent}
              onChange={(e) => onEditContentChange?.(e.target.value)}
              className="postcard-edit-input"
              maxLength={maxChars}
              rows={3}
            />
          ) : (
            <>
              <div className="postcard-content-text">
                {parseContentWithMentions(post.content).map((part, index) => {
                  if (part.type === 'mention' && part.mention) {
                    return (
                      <span key={index} className="postcard-mention-badge">
                        @{part.mention.name}
                      </span>
                    );
                  }
                  return <span key={index}>{part.content}</span>;
                })}
              </div>
              
              {/* URL Preview Cards */}
              {loadingPreviews && (
                <div className="postcard-url-preview-loading">
                  Loading preview...
                </div>
              )}
              
              {!loadingPreviews && urlPreviews.length > 0 && (
                <div className="postcard-url-previews">
                  {urlPreviews.map((preview, index) => (
                    <a
                      key={index}
                      href={preview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="postcard-url-preview"
                    >
                      {preview.image && (
                        <div className="postcard-url-preview-image">
                          <img src={preview.image} alt={preview.title} />
                        </div>
                      )}
                      <div className="postcard-url-preview-content">
                        {preview.title && (
                          <div className="postcard-url-preview-title">{preview.title}</div>
                        )}
                        {preview.description && (
                          <div className="postcard-url-preview-description">
                            {preview.description}
                          </div>
                        )}
                        <div className="postcard-url-preview-domain">
                          {preview.domain}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="postcard-footer">
          <button 
            className={`postcard-like-btn ${post.is_liked ? 'postcard-liked' : ''}`}
            onClick={onLikeToggle}
          >
            <span className="postcard-like-icon">{post.is_liked ? '♥' : '♡'}</span>
            <span className="postcard-like-count">{post.likes_count || 0}</span>
          </button>
          {onCommentClick ? (
            <button 
              className="postcard-comment-btn"
              onClick={onCommentClick}
            >
              <span className="postcard-comment-icon">💬</span>
              <span className="postcard-comment-count">{post.comments_count || 0}</span>
              {isWaitingForAI && (
                <span className="postcard-ai-loading-indicator" title="AI characters are responding...">
                  <span className="postcard-ai-loading-spinner"></span>
                </span>
              )}
            </button>
          ) : (
            <div className="postcard-comment-display">
              <span className="postcard-comment-icon">💬</span>
              <span className="postcard-comment-count">{post.comments_count || 0}</span>
              {isWaitingForAI && (
                <span className="postcard-ai-loading-indicator" title="AI characters are responding...">
                  <span className="postcard-ai-loading-spinner"></span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostCard;

